import React, { useState } from 'react';
import { fmt } from '../../../design/format';
import { ChartTable, TipRow, TipTitle, seqColor, useChartTip, useChartWidth } from './core';

/**
 * Weekday x hour heatmap on the sequential ramp (mockup VL.charts.heatmap), with scale legend.
 * values[row][col]
 */
export function Heatmap({
  rows,
  cols,
  values,
  max: maxProp,
  format = fmt.int,
  labelWidth = 44,
  cellHeight = 30,
  showValues = false,
  metric = 'Calls',
  lowLabel = 'Fewer',
  highLabel = 'More',
  aria = 'Heatmap',
  showTable = false,
}) {
  const [ref, W] = useChartWidth(320);
  const tip = useChartTip();
  const [hover, setHover] = useState(null);

  if (showTable) {
    return (
      <div className="chart" ref={ref}>
        <ChartTable head={['', ...cols]} rows={rows.map((r, i) => [r, ...values[i].map(format)])} />
      </div>
    );
  }

  const top = 22;
  const nC = cols.length;
  const cw = (W - labelWidth) / nC;
  const ch = cellHeight;
  const H = top + rows.length * ch + 4;
  const max = maxProp || Math.max(...values.map((r) => Math.max(...r)), 0) || 1;
  const every = cw < 26 ? 2 : 1;

  return (
    <div className="chart" ref={ref}>
      <svg viewBox={`0 0 ${W} ${H}`} height={H} role="img" aria-label={aria}>
        {cols.map((c, j) =>
          j % every === 0 ? (
            <text key={`c-${c}`} className="tick" x={labelWidth + cw * j + cw / 2} y={14} textAnchor="middle">
              {c}
            </text>
          ) : null
        )}
        {rows.map((r, i) => (
          <g key={`r-${r}`}>
            <text className="tick" x={labelWidth - 8} y={top + ch * i + ch / 2 + 4} textAnchor="end">
              {r}
            </text>
            {cols.map((c, j) => {
              const v = values[i][j];
              const col = seqColor(v / max);
              const isHover = hover && hover[0] === i && hover[1] === j;
              return (
                <React.Fragment key={`${r}-${c}`}>
                  <rect
                    x={labelWidth + cw * j + 1}
                    y={top + ch * i + 1}
                    width={cw - 2}
                    height={ch - 2}
                    rx={3}
                    style={{ fill: col.fill, ...(isHover ? { stroke: 'var(--text)', strokeWidth: 1.5 } : {}) }}
                    onMouseMove={(ev) => {
                      setHover([i, j]);
                      tip.show(
                        <>
                          <TipTitle>{`${r} ${c}`}</TipTitle>
                          <TipRow color="var(--seq-5)" label={metric} value={format(v)} />
                        </>,
                        ev.clientX,
                        ev.clientY
                      );
                    }}
                    onMouseLeave={() => {
                      setHover(null);
                      tip.hide();
                    }}
                  />
                  {showValues && cw > 34 && (
                    <text
                      x={labelWidth + cw * j + cw / 2}
                      y={top + ch * i + ch / 2 + 4}
                      textAnchor="middle"
                      style={{ fontSize: 10.5, fontFamily: 'var(--font-sans)', fontVariantNumeric: 'tabular-nums', fill: col.ink, pointerEvents: 'none' }}
                    >
                      {format(v)}
                    </text>
                  )}
                </React.Fragment>
              );
            })}
          </g>
        ))}
      </svg>
      <div className="row" style={{ gap: 6, marginTop: 10, fontSize: 'var(--fs-caption)', color: 'var(--text-3)' }}>
        <span>{lowLabel}</span>
        {[0, 1, 2, 3, 4, 5, 6].map((i) => (
          <span key={i} style={{ width: 22, height: 10, borderRadius: 2, background: `var(--seq-${i})`, display: 'inline-block', border: '1px solid var(--divider)' }} />
        ))}
        <span>{highLabel}</span>
        <span style={{ marginLeft: 'auto' }}>Peak {format(max)}</span>
      </div>
      {tip.node}
    </div>
  );
}
